// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

import { type Client, formDataBodySerializer, type Options as Options2, type TDataShape } from './client';
import { client } from './client.gen';
import type { CheckRfpCensusDocumentData, CheckRfpCensusDocumentErrors, CheckRfpCensusDocumentResponses, CopyRfpIntakeData, CopyRfpIntakeErrors, CopyRfpIntakeResponses, DownloadCensusTemplateData, DownloadCensusTemplateErrors, DownloadCensusTemplateResponses, GetBpRfpIdByEcosystemIdData, GetBpRfpIdByEcosystemIdErrors, GetBpRfpIdByEcosystemIdResponses, GetEcosystemIdByBpRfpIdData, GetEcosystemIdByBpRfpIdErrors, GetEcosystemIdByBpRfpIdResponses, GetMarketingPresentationInfoData, GetMarketingPresentationInfoErrors, GetMarketingPresentationInfoResponses, GetRfpCensusReadStatusData, GetRfpCensusReadStatusErrors, GetRfpCensusReadStatusResponses, GetRfpCensusValidationData, GetRfpCensusValidationErrors, GetRfpCensusValidationResponses, GetSdlRfpCoverageByEcosystemIdData, GetSdlRfpCoverageByEcosystemIdErrors, GetSdlRfpCoverageByEcosystemIdResponses, UploadRfpCensusDocumentData, UploadRfpCensusDocumentErrors, UploadRfpCensusDocumentResponses } from './types.gen';
import { zCheckRfpCensusDocumentPath, zCheckRfpCensusDocumentResponse, zCopyRfpIntakePath, zCopyRfpIntakeResponse2, zDownloadCensusTemplateResponse, zGetBpRfpIdByEcosystemIdPath, zGetBpRfpIdByEcosystemIdResponse, zGetEcosystemIdByBpRfpIdPath, zGetEcosystemIdByBpRfpIdResponse, zGetMarketingPresentationInfoPath, zGetMarketingPresentationInfoResponse, zGetRfpCensusReadStatusPath, zGetRfpCensusReadStatusQuery, zGetRfpCensusReadStatusResponse, zGetRfpCensusValidationPath, zGetRfpCensusValidationResponse, zGetSdlRfpCoverageByEcosystemIdQuery, zGetSdlRfpCoverageByEcosystemIdResponse, zUploadRfpCensusDocumentBody, zUploadRfpCensusDocumentPath, zUploadRfpCensusDocumentResponse } from './zod.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean, TResponse = unknown> = Options2<TData, ThrowOnError, TResponse> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Get Ecosystem RFP ID by BP RFP ID
 *
 * Returns the Ecosystem (canonical) RFP UUID for a given BP numeric RFP ID.
 *
 * Used by the BFF to translate from the BP-native numeric ID (used in portal URLs
 * and the broker portal frontend) to the Ecosystem UUID (used by downstream
 * services like BTX).
 *
 * Note on URL shape: `/rfps/{id}` always refers to the BP numeric primary key
 * by convention in this API. The inverse lookup — Ecosystem UUID to BP ID — uses
 * `GET /rfps/by-ecosystem-id/{ecoId}` because a UUID cannot occupy the `{id}`
 * slot without ambiguity.
 *
 */
export const getEcosystemIdByBpRfpId = <ThrowOnError extends boolean = false>(options: Options<GetEcosystemIdByBpRfpIdData, ThrowOnError>) => (options.client ?? client).get<GetEcosystemIdByBpRfpIdResponses, GetEcosystemIdByBpRfpIdErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: zGetEcosystemIdByBpRfpIdPath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGetEcosystemIdByBpRfpIdResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/{id}/ecosystem-id',
    ...options
});

/**
 * Get BP RFP ID by Ecosystem RFP ID
 *
 * Returns the BP numeric RFP ID for a given Ecosystem (canonical) RFP UUID.
 *
 * Used by the BFF to translate from an Ecosystem UUID (used by downstream services
 * like BTX) to the BP numeric ID (used in portal URLs and the broker portal
 * frontend).
 *
 * Note on URL shape: because `/rfps/{id}` always refers to the BP primary key by
 * convention, looking up by Ecosystem UUID requires the `by-ecosystem-id` path
 * prefix rather than a plain `{id}` segment. See also
 * `GET /rfps/{id}/ecosystem-id` for the inverse direction.
 *
 */
export const getBpRfpIdByEcosystemId = <ThrowOnError extends boolean = false>(options: Options<GetBpRfpIdByEcosystemIdData, ThrowOnError>) => (options.client ?? client).get<GetBpRfpIdByEcosystemIdResponses, GetBpRfpIdByEcosystemIdErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: zGetBpRfpIdByEcosystemIdPath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGetBpRfpIdByEcosystemIdResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/by-ecosystem-id/{ecoId}',
    ...options
});

/**
 * Get SDL RFP coverage by Ecosystem RFP ID
 *
 * Returns the State Disability & Leaves coverage for a given Ecosystem (canonical)
 * RFP UUID, including the per-state option breakdown used to render SDL selections
 * in the plan builder.
 *
 * Returns 404 when the ecosystem has no SDL coverage — callers should treat this
 * as "no SDL data" rather than an error.
 *
 */
export const getSdlRfpCoverageByEcosystemId = <ThrowOnError extends boolean = false>(options: Options<GetSdlRfpCoverageByEcosystemIdData, ThrowOnError>) => (options.client ?? client).get<GetSdlRfpCoverageByEcosystemIdResponses, GetSdlRfpCoverageByEcosystemIdErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: z.never().optional(),
        query: zGetSdlRfpCoverageByEcosystemIdQuery
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGetSdlRfpCoverageByEcosystemIdResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/by-ecosystem-id/coverage/sdl',
    ...options
});

/**
 * Get marketing presentation info for an RFP
 *
 * Returns all BP-owned data needed to generate a marketing presentation for an RFP.
 *
 * The `{id}` parameter accepts either the BP numeric RFP ID or the RFP's own UUID
 * (`rfp.uuid`). An RFP created or copied through bp-api has a `rfp.uuid` equal to
 * its Ecosystem UUID; legacy RFPs, and copies made before CD-3064, have a
 * different one. The endpoint tries to parse the value as an integer first; if
 * that fails it treats it as a UUID string.
 *
 */
export const getMarketingPresentationInfo = <ThrowOnError extends boolean = false>(options: Options<GetMarketingPresentationInfoData, ThrowOnError>) => (options.client ?? client).get<GetMarketingPresentationInfoResponses, GetMarketingPresentationInfoErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: zGetMarketingPresentationInfoPath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGetMarketingPresentationInfoResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/{id}/marketing-presentation-info',
    ...options
});

/**
 * Get what census processing made of an RFP's census
 *
 * Returns how an RFP's census was read, how it measures against the
 * checklist for the coverages the RFP quotes, and what reading it could
 * not place.
 *
 * The response names nobody and carries no personal data. Values the
 * read could not understand are reported with their sheet row, and with
 * the text as written only for columns holding a vocabulary rather than
 * personal data: an unreadable date of birth, zip code, work zip code or
 * salary is reported by row alone.
 *
 * An RFP that has never had a census read answers 200 with no `source`
 * and no members, which is the ordinary case. An RFP outside the caller's
 * broker scope answers 404.
 *
 */
export const getRfpCensusValidation = <ThrowOnError extends boolean = false>(options: Options<GetRfpCensusValidationData, ThrowOnError>) => (options.client ?? client).get<GetRfpCensusValidationResponses, GetRfpCensusValidationErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: zGetRfpCensusValidationPath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGetRfpCensusValidationResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/{id}/census-validation',
    ...options
});

/**
 * Upload a census for an RFP and start reading it
 *
 * Stores a census file against the RFP and starts reading it into the
 * census tables, recorded as an analyst upload.
 *
 * Answers 202: the file is stored and reading has started, not finished.
 * Reading runs as a workflow, so the census itself is not in the response
 * — poll `GET /rfps/{id}/census-validation` for the result.
 *
 * A read that succeeds replaces the RFP's current census. One that fails
 * leaves the previous census in place and records the attempt.
 *
 */
export const uploadRfpCensusDocument = <ThrowOnError extends boolean = false>(options: Options<UploadRfpCensusDocumentData, ThrowOnError>) => (options.client ?? client).post<UploadRfpCensusDocumentResponses, UploadRfpCensusDocumentErrors, ThrowOnError>({
    ...formDataBodySerializer,
    requestValidator: async (data) => await z.object({
        body: zUploadRfpCensusDocumentBody,
        path: zUploadRfpCensusDocumentPath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zUploadRfpCensusDocumentResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/{id}/census-document',
    ...options,
    headers: {
        'Content-Type': null,
        ...options.headers
    }
});

/**
 * Get the most recent attempt to read an RFP's census
 *
 * Returns the most recent read attempt, whether or not it produced the
 * census the RFP is quoted on.
 *
 * Deliberately small: a caller waiting for an upload to be read asks for
 * this repeatedly, and fetches the census itself only once the answer
 * changes.
 *
 * A read that finds no members is recorded without replacing the current
 * census, so this is the only place such a read can be seen. `GET
 * /rfps/{id}/census-validation` describes the census; this describes the
 * attempt.
 *
 * Answers 204 where no read has ever been attempted.
 *
 */
export const getRfpCensusReadStatus = <ThrowOnError extends boolean = false>(options: Options<GetRfpCensusReadStatusData, ThrowOnError>) => (options.client ?? client).get<GetRfpCensusReadStatusResponses, GetRfpCensusReadStatusErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: zGetRfpCensusReadStatusPath,
        query: zGetRfpCensusReadStatusQuery.optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGetRfpCensusReadStatusResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/{id}/census-read-status',
    ...options
});

/**
 * Tell a broker what to change in a census file they uploaded
 *
 * Checks a census file attached to an RFP against the lines of coverage
 * the RFP quotes, with the checklist the analyst census page uses, and
 * returns the changes that would let us read all of it. Meant for the
 * RFP wizard, right after a broker uploads a census. It never blocks a
 * submission.
 *
 * A document already checked against the RFP's current products answers
 * from the stored check. Otherwise the file is read again from
 * SharePoint and the new check is stored, so what each broker was told
 * stays on record. No census members are stored.
 *
 * Gated by the `census-upload-check` flag, an object keyed by broker id:
 * the broker's external id or its Salesforce id. An empty object checks
 * every broker and one listing ids checks only those. The wizard reads
 * the same flag and only calls for brokers it admits; for any other the
 * endpoint answers 403.
 *
 */
export const checkRfpCensusDocument = <ThrowOnError extends boolean = false>(options: Options<CheckRfpCensusDocumentData, ThrowOnError>) => (options.client ?? client).post<CheckRfpCensusDocumentResponses, CheckRfpCensusDocumentErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: zCheckRfpCensusDocumentPath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zCheckRfpCensusDocumentResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/{id}/documents/{documentId}/census-check',
    ...options
});

/**
 * Download the blank census workbook
 *
 * The census template a broker can fill in: one sheet with the headings
 * the census parser reads without guessing. The RFP wizard links to it
 * beside a census check's suggestions.
 *
 */
export const downloadCensusTemplate = <ThrowOnError extends boolean = false>(options?: Options<DownloadCensusTemplateData, ThrowOnError>) => (options?.client ?? client).get<DownloadCensusTemplateResponses, DownloadCensusTemplateErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: z.never().optional(),
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'blob',
    responseValidator: async (data) => await zDownloadCensusTemplateResponse.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/census-template',
    ...options
});

/**
 * Copy only what the broker submitted with an RFP
 *
 * Creates a new RFP from the broker's intake of an existing one:
 *
 * - the intake data in the broker schema: details, employer, requested
 * and current coverages (deleted coverages are left out), carrier
 * recipients, and the coverages' links to the client's policies;
 * - the document rows (`rfpdocument`, with its path moved to the copy's
 * folder, and the per-coverage and additional documents), census files
 * included, and the broker's documents in SharePoint: the `documents`
 * folder and the analyst's `Documents from Broker` folder, copied
 * together into the copy's `documents` folder;
 * - the structured census: the broker and Employee Navigator ingestions,
 * and the current census with its members and coverages;
 * - the AI Dox documents, with their types and coverage links, so RUMI
 * can cite its values against them. The copy waits for these, so they
 * exist when the response arrives;
 * - when no census came across (an RFP submitted before censuses were
 * read on submission has none to copy), the census file, read onto the
 * copy as submission would read it: the source's latest document typed
 * `census` and not infected, passing over a file an analyst census
 * upload read. The file is downloaded from the source RFP's path, or
 * from the same stored name in its `Documents from Broker` folder when
 * that fails, parsed, and stored on the copy as a `broker_upload`
 * census. The copy waits for this too. `census` in the response says
 * where the copy's census came from.
 *
 * The analyst's work is not copied: no BTX build (plans, classes, rates,
 * notes), no analyst or proposal folders, no AI Dox output (benefit
 * plans and processing jobs), no assignment or status history. An analyst's census upload comes across as a file
 * but not as a census: its ingestion is left out, and a current census
 * read from it is not copied, since it replaced the broker's.
 * The copy starts as `pendingAssignment`, and BTX builds it from its
 * intake when it is first opened.
 *
 * Used to replay an RFP for RUMI's evaluation (ADR-0001 in
 * ai-docs-playground). `POST /rfps/{id}/copy` copies everything.
 *
 * `{id}` takes the BP numeric RFP ID, the number shown in the portal. An
 * RFP UUID or Ecosystem UUID also works.
 *
 * The SharePoint documents are copied in the background after the
 * response; the new RFP's folder fills over the following minutes.
 *
 * Requires the `admin` or `consultant` role.
 *
 */
export const copyRfpIntake = <ThrowOnError extends boolean = false>(options: Options<CopyRfpIntakeData, ThrowOnError>) => (options.client ?? client).post<CopyRfpIntakeResponses, CopyRfpIntakeErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        path: zCopyRfpIntakePath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zCopyRfpIntakeResponse2.parseAsync(data),
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/rfps/{id}/intake-copy',
    ...options
});
